import { ORDER_COMPLETED, ORDER_REJECTED } from '@constant/order';
import { getItemsList } from '@util/dataFilterService';

export const EN_LANG = { languageId: 'en', displayName: 'English' };

export type BillLanguage = { languageId: string; displayName: string };

/**
 * The printed bill is produced from the order exactly as the customer settled it, so a completed or
 * rejected order is stamped with the moment it reached that state and anything still open is
 * stamped with now.
 */
export const getSettledTiming = (order: any): string => {
    const lastStatus = order?.statuses?.[order?.statuses?.length - 1];
    if (lastStatus?.state != ORDER_COMPLETED && lastStatus?.state != ORDER_REJECTED) {
        return new Date().toLocaleString();
    }
    return typeof lastStatus.createdOn == 'string'
        ? new Date(lastStatus.createdOn).toLocaleString()
        : lastStatus.createdOn.toLocaleString();
};

/**
 * Arabic is the one language the bill is expected to default to when a store has it enabled;
 * otherwise the first active language wins and English is always offered as an alternative.
 */
export const resolveBillLanguages = (languages: any): {
    languagesList: BillLanguage[];
    activeLanguage: BillLanguage;
} => {
    const active = Array.isArray(languages) ? languages.filter((l: any) => l?.active) : [];
    if (!active.length) {
        return { languagesList: [EN_LANG], activeLanguage: EN_LANG };
    }

    const arabic = active.find((l: any) => l.languageId === 'ar');
    return {
        languagesList: [EN_LANG, ...active],
        activeLanguage: arabic || active[0]
    };
};

export const resolveBillRequest = (activeLanguage: BillLanguage, basicConfig: any) => {
    const combinePrintBill = activeLanguage.languageId !== 'en'
        ? Boolean(basicConfig?.combine_bill)
        : false;

    return {
        combinePrintBill,
        languageCode: combinePrintBill ? `en,${activeLanguage.languageId}` : activeLanguage.languageId
    };
};

const getLangName = (catalogueItem: any, langId: string, fallbackName: string) => {
    if (!catalogueItem) return fallbackName;
    if (langId === 'en' || !catalogueItem.languages || !catalogueItem.languages[langId]) {
        return catalogueItem.name ?? fallbackName;
    }
    return catalogueItem.languages[langId].name ?? fallbackName;
};

/**
 * The bill service renders each line in every offered language, so every product carries the
 * translations looked up from the catalogue. Built in one place because the server prefetch and the
 * in-page language switcher must send byte-identical payloads.
 */
export const buildBillOrder = async (
    order: any,
    categories: any,
    languagesList: BillLanguage[]
): Promise<any> => {
    const billOrder = { ...order };
    const itemsList: any = await getItemsList(categories || []);

    billOrder.products = (billOrder.products || []).map((item: any) => {
        const catalogueItem = itemsList?.find(
            (i: any) => i?.name?.toLowerCase() === item?.name?.toLowerCase()
        );
        const languagesData = {};
        languagesList.forEach((lang) => {
            languagesData[lang.languageId] = { name: getLangName(catalogueItem, lang.languageId, item?.name) };
        });
        return { ...item, languagesData };
    });

    return billOrder;
};
